/* eslint-disable */

type Option = {
  name: string;
  value: string;
  disabled?: boolean;
  data?: string;
};
class TypeaheadDropdown extends HTMLElement {
  // Initialize some private variables here if needed, or inside the constructor.
  readonly _selectContainer: HTMLElement;
  readonly _typeaheadInput: HTMLInputElement;
  readonly _typeaheadOptions: HTMLElement;
  readonly _typeaheadFavorites: HTMLElement;
  currentFocus: number;
  currentValue: string;
  minCharacters: number;
  constructor() {
    // This is always needed to pull everything from the HTMLElement class.
    super();

    // Create shadowRoot element, CSS, and HTML, in that order.
    const template = document.createElement('template');
    template.innerHTML += `
        <style>
        *, *::before, *::after {
            box-sizing:border-box;
            color:var(--neutral-70);
        }

        [typeahead-container] {
            position:relative;
        }

        [typeahead-input] {
            background-color:var(--white);
            border:1px solid var(--neutral-40);
            height:2.5rem;
            line-height:2.75;
            padding-left:1rem;
            padding-right: 2.5rem;
            font-family:'Metropolis', Arial, sans-serif;
            font-size: 0.875rem;
            background-image:var(--chevron-down-light); /* Potential Problem - Make the variable here or use it from the global scope? */
            background-repeat:no-repeat;
            background-position:right 0.75rem center;
            background-size:1rem 1rem;
            /* cursor:pointer; */
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            width: 100%;
            box-sizing: border-box;
        }

        [typeahead-input]:hover {
          box-shadow: inset 0px 0px 0px 1px #767c80;
        }

        [typeahead-container][is--open] [typeahead-input] {
            background-image: var(--chevron-up-light);
        }

        [typeahead-input]:focus,
        [typeahead-options]:focus,
        [typeahead-favorites]:focus {
            border-color:var(--brand-50);
            outline:1px solid var(--brand-50);
            box-shadow: none;
        }

        [typeahead-input][aria-expanded=true] {
            border-color:var(--brand-50);
            outline:none;
            box-shadow: none;
        }


        [typeahead-options], [typeahead-favorites] {
            background-color:#fff;
            border:1px solid var(--neutral-40);
            border-top:0;
            font-size: 0.875rem;
            max-height:300px;
            margin: 0;
            padding: 0;
            overflow-y:auto;
            list-style: none;
        }

        [typeahead-options] [typeahead-option],
        [typeahead-favorites] [typeahead-option]
         {
          font-weight: 400;
          font-size: 0.875rem;
          line-height: 1.2em;
          color: #3A4042;
          padding: 12px 5px 12px 15px;
          user-select: none;
        }

        [typeahead-options]  [typeahead-option]:hover,
        [typeahead-favorites]  [typeahead-option]:hover,
        [typeahead-options]  [typeahead-option]:active,
        [typeahead-favorites]  [typeahead-option]:active,
        [typeahead-options] li.is--active,
        [typeahead-favorites] li.is--active {
          background-color:var(--lp-10-gray-hover);
        }

        [typeahead-options] li:active,
        [typeahead-favorites] li:active
        {
          background-color:var(--lp-10-gray-pressed);
        }

        [typeahead-container][is--open] [options-container] {
            display:block;
            font-family:'Metropolis', Arial, sans-serif;
        }

        :host([disabled=true]) {
            pointer-events:none;
            user-select:none;
        }

        :host([disabled=true]) [typeahead-input] {
            background-color:var(--util-disabled-01-gray);
            color:var(--neutral-40);
            background-image:var(--chevron-down-light);
            cursor:not-allowed;
        }

        [typeahead-options] li.typeahead_option_disabled {
            background-color:var(--util-disabled-01-gray);
            color:var(--neutral-40);
            cursor:not-allowed;
            pointer-events: none;
            user-select: none;
        }



        :host(.search) [typeahead-input] {
          background-image: url('/assets/images/icons/search.svg');
        }

        :host(.no-icon) [typeahead-input] {
          background-image: none;
        }

        [options-container]{
          position:absolute;
          display:none;
          width: 100%;
          top:calc(100%);
          left:0;
          right:0;
          z-index:1;
        }

        [typeahead-favorites] [typeahead-option] img{
          width: 18px;
          height: 24px;
          margin-right: 8px;
        }

        [typeahead-favorites] [typeahead-option]{
          display: flex;
          align-items: center;
        }

        [typeahead-favorites]{
          padding 0 10px;
        }

        [favorites-header]{
          padding: 5px 5px 5px 15px;
          height: 24px;
          width: 100%;
          font-size: 12px;
          font-weight: 600;
          font-stretch: normal;
          font-style: normal;
          line-height: 1.5;
          letter-spacing: normal;
          text-align: left;
          color: var(--hp-gray-50);
        }
        </style>`;
    template.innerHTML += `
        <div typeahead-container>
          <input typeahead-input type="text" aria-autocomplete="list" aria-owns="listbox" aria-expanded="false" aria-haspopup="listbox" role="combobox" maxlength="50" tabindex="0" />
          <div options-container>
            <ul typeahead-favorites role="listbox"></ul>
            <ul typeahead-options role="listbox"></ul>
          </div>
        </div>`;

    // Create the shadowRoot and append the template.
    this.attachShadow({ mode: 'open' });
    this.shadowRoot.appendChild(template.content.cloneNode(true));

    // Get specific elements from the shadowRoot.
    // Do these attributes need set or get?
    this._selectContainer = this.shadowRoot.querySelector('[typeahead-container]');
    this._typeaheadInput = this.shadowRoot.querySelector('[typeahead-input]');
    this._typeaheadOptions = this.shadowRoot.querySelector('[typeahead-options]');
    this._typeaheadFavorites = this.shadowRoot.querySelector('[typeahead-favorites]');

    // For skeleton display.
    this._typeaheadInput.placeholder = 'Select';

    // Default minimum character
    this.minCharacters = 3;

    // Add a listener to the first child of the select dropdown.
    // TODO: Clean up this add EventListener, this is a memory leak
    this._selectContainer.addEventListener('click', (e) => this._click(e));
    this._typeaheadInput.addEventListener('keyup', (e: any) => {
      if (e?.target?.value) {
        this.hideFavourite();
      } else {
        this._displayFavourites();
      }
      this._keyup(e);
    });

    // Bind functions necessary for the custom element to the class.
    this._click = this._click.bind(this);
    this._getAllOptions = this._getAllOptions.bind(this);
    this._displayMatches = this._displayMatches.bind(this);
    this._findMatches = this._findMatches.bind(this);
    this._documentClickHandler = this._documentClickHandler.bind(this);
    this._focusOutClickHandler = this._focusOutClickHandler.bind(this);
  }

  /* Called when component is loaded */
  /* Getting called three times for some reason */
  connectedCallback() {
    const _self = this;
    setTimeout(function () {
      if (_self.currentValue) {
        _self._typeaheadInput.value = _self.currentValue;
      }
      // Was getting null value, so moved the label check inside setTimeout() to allow the elements to load.
      _self._typeaheadInput.setAttribute('aria-label', _self.getAttribute('aria-label'));
      _self._typeaheadInput.setAttribute('aria-errormessage', _self.getAttribute('aria-errormessage'));
    }, 500);
    // Close the dropdown if clicked anywhere outside the dropdown.
    document.addEventListener('click', this._documentClickHandler);

    // Close the dropdown when the input lose the focus
    this._typeaheadInput.addEventListener('focusout', this._focusOutClickHandler);
    this._typeaheadInput.addEventListener('focus', (e: any) => {
      if (e?.target?.value) {
        this.hideFavourite();
      } else {
        this._displayFavourites();
      }
    });
  }

  public setValue(value: any): void {
    this._typeaheadInput.value = value;
  }

  private _documentClickHandler(e) {
    if (e.target !== this) {
      this._selectContainer.removeAttribute('is--open');
      this._typeaheadInput.setAttribute('aria-expanded', 'false');
    }
  }

  private _focusOutClickHandler(e) {
    if (!e.relatedTarget?.hasAttribute('typeahead-option')) {
      this._selectContainer.removeAttribute('is--open');
      this._typeaheadInput.setAttribute('aria-expanded', 'false');
    }
  }

  private _findMatches(wordToMatch, options): Option[] {
    return options.filter((option: Option) => {
      const regex = new RegExp(wordToMatch, 'gi');
      return option.value.match(regex);
    });
  }

  private _displayMatches(value: string) {
    this.currentFocus = -1;
    const minChar = this.minCharacters;
    const matchArray: Option[] = this._findMatches(value, this.options);
    if (matchArray.length) {
      this._selectContainer.setAttribute('is--open', 'true');
      this._typeaheadInput.setAttribute('aria-expanded', 'true');
      const html = matchArray
        .map((option: Option, index: number) => {
          const regex = new RegExp(value, 'gi');
          const optionName = option.value.replace(regex, function (match) {
            return value.length >= minChar && !option.disabled
              ? `<strong
                   data-option-name="${option.name}"
                   data-option-value="${option.value}"
                   data-option-data="${option.data}">
                   ${matchCase(value, match)}
                 </strong>`
              : match;
          });
          return `<li
                    typeahead-option
                    role="option"
                    tabindex="-1"
                    aria-selected="false"
                    data-option-name="${option.name}"
                    data-option-value="${option.value}"
                    data-option-data="${option.data}"
                    class="typeahead_option_${option.disabled ? 'disabled' : ''}"
                    id="typeahead_option_${(this.favorites?.length || 0) + index}">
                    ${optionName}
                  </li>`; // TODO: Need to make this more generic if web component is used in other places
        })
        .join('');
      this._typeaheadOptions.innerHTML = html;
      // const allOptions: HTMLElement[] = this._getAllOptions();
      // this.options.map((option, index) => {
      //   if (option?.disabled) {
      //     allOptions[index]?.classList?.add('typeahead_option_disabled');
      //     this._selectContainer.removeEventListener('click', this._click);
      //   }
      // });

      this._typeaheadOptions.querySelectorAll('li').forEach(i => {
        i.onclick = () => this._typeaheadOptions.dispatchEvent(
          new CustomEvent(
            'optionSelected',
            {
              detail: { value: i.dataset.optionValue, name: i.dataset.optionName, data: i.dataset.optionData },
              bubbles: true,
              composed: true
            }
          )
        );
      });

      if (matchArray.length === 1 && value.toUpperCase() === matchArray[0].value.toUpperCase()) {
        const activeOptions: HTMLElement[] = this._getAllOptions();
        // MD: No longer required to automatically select option when there's only one match.
        // Keeping this commented in case the requirements change for another module.
        // activeOptions[0].click();
      } else {
        this._typeaheadOptions.dispatchEvent(
          new CustomEvent('change', { detail: { value: value, name: value }, bubbles: true, composed: true })
        );
      }
    } else {
      this._typeaheadOptions.innerHTML = null;
      this._typeaheadOptions.dispatchEvent(
        new CustomEvent('change', { detail: { value: value, name: value }, bubbles: true, composed: true })
      );
    }

    /* Helper function to persist case on bolding match */
    function matchCase(text, pattern) {
      var result = '';
      for (var i = 0; i < text.length; i++) {
        var c = text.charAt(i);
        var p = pattern.charCodeAt(i);

        if (p >= 65 && p < 65 + 26) {
          result += c.toUpperCase();
        } else {
          result += c.toLowerCase();
        }
      }
      return result;
    }
  }

  private _keyup(e) {
    const activeOptions: HTMLElement[] = this._getAllOptions();
    if (e.keyCode == 40) {
      // DOWN key
      this.currentFocus++;
      this._addActive(activeOptions);
    } else if (e.keyCode == 38) {
      // UP key
      this.currentFocus--;
      this._addActive(activeOptions);
    } else if (e.keyCode == 13) {
      // ENTER key - prevent form submission
      e.preventDefault();
      if (this.currentFocus > -1) {
        if (activeOptions) {
          activeOptions[this.currentFocus].click();
        }
      }
    } else {
      this._displayMatches(this._typeaheadInput.value);
    }
  }

  private _addActive(activeOptions: HTMLElement[]) {
    if (!activeOptions) return false;
    const options = [...this.favorites, ...this.options];

    // Remove is--active class on all options
    activeOptions.forEach((option) => {
      option.classList.remove('is--active');
    });

    if (this.currentFocus >= options.length) this.currentFocus = 0;
    if (this.currentFocus < 0) this.currentFocus = options.length - 1;
    // Add active class on current option
    activeOptions[this.currentFocus].classList.add('is--active');
    activeOptions[this.currentFocus].scrollIntoView({ block: 'nearest' });
  }

  private _click(e) {
    if (e.target === this._typeaheadInput) {
      this._displayMatches(this._typeaheadInput.value);
    } else {
      const optionValue = e.target.dataset.optionValue;
      const optionName = e.target.dataset.optionName;
      const optionData = e.target.dataset.optionData;
      // Must be an option clicked.
      this._selectContainer.setAttribute('value', e.target.value);
      this._typeaheadInput.setAttribute('aria-activedescendant', e.target.id);
      this._typeaheadInput.value = e.target.dataset.optionName;
      this._getAllOptions().map((option) => option.setAttribute('aria-selected', 'false'));
      e.target.setAttribute('aria-selected', 'true');
      // Close dropdown
      this._selectContainer.removeAttribute('is--open');
      this._typeaheadInput.setAttribute('aria-expanded', 'false');
      // Send event to framework
      e.target.dispatchEvent(
        new CustomEvent('select', { detail: { value: optionValue, name: optionName, data: optionData }, bubbles: true, composed: true })
      );
    }
  }

  private _getAllOptions(): HTMLElement[] {
    return Array.from(this.shadowRoot.querySelectorAll('[typeahead-option]'));
  }

  get disabled() {
    return this.getAttribute('disabled');
  }

  set disabled(v) {
    this.setAttribute('disabled', v);
    this._typeaheadInput.disabled = (<any>v as boolean);
  }

  get value() {
    return this.getAttribute('value');
  }

  set value(v) {
    this.setAttribute('value', v);
  }

  get options() {
    return this.getAttribute('options') ? JSON.parse(this.getAttribute('options')) : [];
  }

  set options(v) {
    this.setAttribute('options', v ? JSON.stringify(v) : '');
  }

  get placeholder() {
    return this.getAttribute('placeholder');
  }

  set placeholder(v) {
    this.setAttribute('placeholder', v);
  }

  static get observedAttributes() {
    return ['disabled', 'value', 'options', 'placeholder', 'minlength', 'favorites', 'favoriteHeader', 'ariainvalid'];
  }

  get favorites() {
    return this.getAttribute('favorites') ? JSON.parse(this.getAttribute('favorites')) : [];
  }

  set favorites(v) {
    this.setAttribute('favorites', v ? JSON.stringify(v) : '');
  }

  get favoriteHeader() {
    return this.getAttribute('favoriteHeader');
  }

  set favoriteHeader(v) {
    this.setAttribute('favoriteHeader', v);
  }

  get ariainvalid() {
    return this.getAttribute('ariainvalid');
  }

  set ariainvalid(v) {
    this._typeaheadInput.setAttribute('aria-invalid', v);
  }

  attributeChangedCallback(attributeName, oldValue, newValue) {
    // Handle a form control value change from the framework when an option is selected or preselected.
    if (attributeName === 'placeholder') {
      this._typeaheadInput.placeholder = newValue;
    } else if (attributeName === 'value') {
      const options: HTMLElement[] = this._getAllOptions();
      if (!newValue) {
        // Reset button if no preselected option. Value is blank here.
        // resetting the input here sine options length will be 0
        this._typeaheadInput.removeAttribute('aria-activedescendant');
        this._typeaheadInput.value = null;

        options.forEach((el: HTMLElement) => {
          el.setAttribute('aria-selected', 'false');
        });
      } else {
        // Bind value on regular option click or bind preselected value on form reset.
        // Framework is changing the value property through the (change) function, thus this is fired when an
        // option is clicked and changes the value property of the form.
        options.forEach((el: HTMLElement) => {
          if (newValue === el.dataset.optionValue) {
            this._typeaheadInput.setAttribute('aria-activedescendant', el.id);
            this._typeaheadInput.value = el.dataset.optionName;
            el.setAttribute('aria-selected', 'true');
          } else {
            // Reset non-preselect options to not be the preselect option.
            el.setAttribute('aria-selected', 'false');
          }
        });
      }
    } else if (attributeName === 'options') {
      if (this.options?.length > 0 && this.shadowRoot.activeElement === this._typeaheadInput) {
        this._displayMatches(this._typeaheadInput.value);
      } else {
        this._typeaheadOptions.innerHTML = '';
      }
      // When the incoming dropdown options are changed and the typeaheadInput is on focus
    } else if (attributeName === 'minlength') {
      this.minCharacters = +newValue;
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._documentClickHandler);
    this._selectContainer.removeEventListener('click', this._click);
    this._typeaheadInput.removeEventListener('focusout', this._focusOutClickHandler);
  }

  focus() {
    this._typeaheadInput.focus();
  }

  private _displayFavourites(value = '') {
    if (this._getAllOptions().length === 0) {
      this.currentFocus = -1;
    }

    const minChar = this.minCharacters;
    const matchArray: Option[] = this.favorites;
    if (matchArray.length) {
      this._selectContainer.setAttribute('is--open', 'true');
      this._typeaheadInput.setAttribute('aria-expanded', 'true');
      const html = matchArray
        .filter((option) => !!option)
        .map((option: Option, index: number) => {
          const regex = new RegExp(value, 'gi');
          const optionName = option?.value?.replace(regex, function (match) {
            return value.length >= minChar
              ? `<strong
                   data-option-name="${option.name}"
                   data-option-value="${option.value}"
                   data-option-data="${option.data}">
                   ${matchCase(value, match)}
                 </strong>`
              : match;
          });
          return `<li
                    typeahead-option
                    class="typeahead-option"
                    role="option"
                    tabindex="-1"
                    aria-selected="false"
                    data-option-name="${option.name}"
                    data-option-value="${option.value}"
                    data-option-data="${option.data}"
                    id="typeahead_option_${index}">
                    ${option.name}
                  </li>`; // TODO: Need to make this more generic if web component is used in other places
        })
        .join('');
      if (html && html.length) {
        this._typeaheadFavorites.innerHTML = `
        <li favorites-header id="typeahead_option_header"><strong>${`${this.favoriteHeader}`.toUpperCase() || 'FAVORITES'
          }</strong></li>
        ${html}`;
      }
    }

    /* Helper function to persist case on bolding match */
    function matchCase(text, pattern) {
      var result = '';
      for (var i = 0; i < text.length; i++) {
        var c = text.charAt(i);
        var p = pattern.charCodeAt(i);

        if (p >= 65 && p < 65 + 26) {
          result += c.toUpperCase();
        } else {
          result += c.toLowerCase();
        }
      }
      return result;
    }
  }

  private hideFavourite() {
    this._typeaheadFavorites.innerHTML = '';
  }
}
window.customElements.define('typeahead-dropdown', TypeaheadDropdown);
